<script setup>
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import { ref, computed } from "vue";
import { watch } from "vue";

let emits = defineEmits(["updated"]);
let props = defineProps(["message"]);

let colorInput = ref();
let editor = useEditor({
	content: props.message,
	extensions: [
		StarterKit,
		Underline,
		TextStyle,
		Color,
		Highlight,
		Placeholder.configure({
			placeholder: "Skriv noget …",
		}),
	],
	onFocus({ editor }) {
		editor.options.element.parentElement.classList.add("focused");
	},
	onBlur({ editor }) {
		editor.options.element.parentElement.classList.remove("focused");
	},
	onCreate({ editor }) {
		if (props.message) {
			editor.commands.setContent(getEditorInput(), true, {
				preserveWhitespace: true,
			});
		}
		emits("updated", getEscapedOutput());
	},
	onUpdate({ editor }) {
		emits("updated", getEscapedOutput());
	},
});

watch(
	() => props.message,
	(newValue) => {
		if (editor) {
			editor.commands.setContent(newValue);
		}
	},
);

const getSelectionColor = computed(() => {
	let color =
		editor.value?.getAttributes("textStyle").color || "rgb(0, 0, 0)";
	return (
		"#" +
		color.match(/\d+/g).map((x) => (+x).toString(16).padStart(2, 0)).join``
	);
});

const getEscapedOutput = () => {
	return editor.value
		.getHTML()
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/ {2}/g, "&nbsp;&nbsp;")
		.replace(/'/g, "&#039;");
};

const getEditorInput = () => {
	return props.message
		? props.message
				.replace(/&amp;/g, "&")
				.replace(/&lt;/g, "<")
				.replace(/&gt;/g, ">")
				.replace(/&quot;/g, '"')
				.replace(/&#039;/g, "'")
		: "";
};
</script>

<template>
	<div class="editor-container">
		<div class="toolbar">
			<button
				type="button"
				@click="editor.chain().focus().toggleBold().run()"
				:class="{ 'is-active': editor?.isActive('bold') }"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					width="24"
					height="24"
				>
					<path fill="none" d="M0 0h24v24H0z" />
					<path
						d="M8 11h4.5a2.5 2.5 0 1 0 0-5H8v5zm10 4.5a4.5 4.5 0 0 1-4.5 4.5H6V4h6.5a4.5 4.5 0 0 1 3.256 7.606A4.498 4.498 0 0 1 18 15.5zM8 13v5h5.5a2.5 2.5 0 1 0 0-5H8z"
						fill="#000"
					/>
				</svg>
			</button>
			<button
				type="button"
				@click="editor.chain().focus().toggleItalic().run()"
				:class="{ 'is-active': editor?.isActive('italic') }"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					width="24"
					height="24"
				>
					<path fill="none" d="M0 0h24v24H0z" />
					<path
						d="M15 20H7v-2h2.927l2.116-12H9V4h8v2h-2.927l-2.116 12H15z"
						fill="#000"
					/>
				</svg>
			</button>
			<button
				type="button"
				@click="editor.chain().focus().toggleUnderline().run()"
				:class="{ 'is-active': editor?.isActive('underline') }"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					width="24"
					height="24"
				>
					<path fill="none" d="M0 0h24v24H0z" />
					<path
						d="M8 3v9a4 4 0 1 0 8 0V3h2v9a6 6 0 1 1-12 0V3h2zM4 20h16v2H4v-2z"
						fill="#000"
					/>
				</svg>
			</button>
			<div class="color-container">
				<button
					type="button"
					@click="colorInput.click()"
					:class="{
						'is-active':
							editor?.isActive('textStyle') &&
							getSelectionColor !== '#000000',
					}"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						width="24"
						height="24"
					>
						<path fill="none" d="M0 0h24v24H0z" />
						<path
							d="M15.246 14H8.754l-1.6 4H5l6-15h2l6 15h-2.154l-1.6-4zm-.8-2L12 5.885 9.554 12h4.892zM3 20h18v2H3v-2z"
							fill="#000"
						/>
					</svg>
				</button>
				<input
					ref="colorInput"
					type="color"
					:value="getSelectionColor"
					@change="
						(e) =>
							editor
								.chain()
								.focus()
								.setColor(e.target.value)
								.run()
					"
				/>
			</div>
			<button
				type="button"
				@click="editor.chain().focus().toggleHighlight().run()"
				:class="{ 'is-active': editor?.isActive('highlight') }"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					width="24"
					height="24"
				>
					<path fill="none" d="M0 0h24v24H0z" />
					<path
						d="M8 21v-2h3V5H8V3h8v2h-3v14h3v2H8zM18.05 7.05L23 12l-4.95 4.95-1.414-1.414L20.172 12l-3.536-3.536L18.05 7.05zm-12.1 0l1.414 1.414L3.828 12l3.536 3.536L5.95 16.95 1 12l4.95-4.95z"
					/>
				</svg>
			</button>
			<button
				type="button"
				@click="
					editor.chain().focus().clearNodes().unsetAllMarks().run()
				"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					width="24"
					height="24"
				>
					<path fill="none" d="M0 0h24v24H0z" />
					<path
						d="M8.586 8.858l-4.95 4.95 5.194 5.194H10V19h1.172l3.778-3.778-6.364-6.364zM10 7.444l6.364 6.364 2.828-2.829-6.364-6.364L10 7.444zM14 19h7v2h-9l-3.998.002-6.487-6.487a1 1 0 0 1 0-1.414L12.12 2.494a1 1 0 0 1 1.415 0l7.778 7.778a1 1 0 0 1 0 1.414L14 19z"
					/>
				</svg>
			</button>
		</div>
		<editor-content id="editor" :editor="editor" />
	</div>
</template>

<style>
@import url("../../app-assets/css/text-editor.css");

.ProseMirror p.is-editor-empty:first-child::before {
	color: #adb5bd;
	content: attr(data-placeholder);
	float: left;
	height: 0;
	pointer-events: none;
}
</style>
